import type { AmazonItemDeal, Deal } from '../../types/deals';
import { amazonItemTitle, matchesAmazonItemFilters } from './amazonItemFilters';
import {
  AMAZON_STORE_SLUG,
  dealListKey,
  matchesDealFilters,
  type DealFilters,
  type DealSearchFields,
} from './dealFilters';

export type CuratedDealRow = { kind: 'curated'; deal: Deal; path: string; feedIndex: number };
export type AmazonItemDealRow = { kind: 'amazonItem'; item: AmazonItemDeal };
export type DealRow = CuratedDealRow | AmazonItemDealRow;

type DealOrderFields = Pick<Deal, 'effective_at' | 'expires_at'>;

/** The order deals.json is written in. Mirrors compareDeals in apps/deals-processor/src/sort.ts. */
export function compareDealOrder(a: DealOrderFields, b: DealOrderFields): number {
  const aHasExpiry = !!a.expires_at;
  const bHasExpiry = !!b.expires_at;
  if (aHasExpiry !== bHasExpiry) return aHasExpiry ? -1 : 1;
  if (!aHasExpiry) return 0;
  if (!a.effective_at && !b.effective_at) return 0;
  if (!a.effective_at) return 1;
  if (!b.effective_at) return -1;
  return new Date(b.effective_at).getTime() - new Date(a.effective_at).getTime();
}

function orderFields(row: DealRow): DealOrderFields {
  return row.kind === 'curated'
    ? row.deal
    : { expires_at: row.item.deal.endTime ?? null, effective_at: row.item.deal.startTime ?? null };
}

/** Curated rows keep their deals.json order; each item deal goes before the first curated row it sorts ahead of. */
export function mergeDealRows(
  curated: Omit<CuratedDealRow, 'kind'>[],
  items: AmazonItemDeal[],
): DealRow[] {
  const itemRows = items
    .map((item): DealRow => ({ kind: 'amazonItem', item }))
    .sort((a, b) => compareDealOrder(orderFields(a), orderFields(b)));
  const merged: DealRow[] = [];
  let next = 0;
  for (const fields of curated) {
    const row: DealRow = { kind: 'curated', ...fields };
    while (
      next < itemRows.length &&
      compareDealOrder(orderFields(itemRows[next]), orderFields(row)) < 0
    ) {
      merged.push(itemRows[next++]);
    }
    merged.push(row);
  }
  return merged.concat(itemRows.slice(next));
}

export function matchesDealRow(row: DealRow, filters: DealFilters, now = Date.now()): boolean {
  return row.kind === 'curated'
    ? matchesDealFilters(row.deal, filters, now)
    : matchesAmazonItemFilters(row.item, filters, now);
}

export function dealRowSearchFields(row: DealRow): DealSearchFields {
  return row.kind === 'curated'
    ? row.deal
    : { retailer: AMAZON_STORE_SLUG, name: amazonItemTitle(row.item), description: null };
}

// Pass the Amazon item query only while it is shown: a disabled query keeps its last error.
export function dealListFailed(
  rowCount: number,
  curated: { isError: boolean },
  amazonItems: { isError: boolean } | undefined,
): boolean {
  return rowCount === 0 && (curated.isError || !!amazonItems?.isError);
}

export function dealRowKey(row: DealRow): string {
  return row.kind === 'curated'
    ? dealListKey(row.deal, row.path, row.feedIndex)
    : `amazon:${row.item.asin}`;
}
